import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material'
import { useFormik } from 'formik'
import { useState } from 'react'
import * as yup from 'yup'
import {
	BaseFormPackageProcessorInstanceModel,
	ConfigurableFormPackageProcessor,
} from '../../../../../../../../api/DTOtemp'
import { FixedHeaderWithHint } from '../../../../../../../../utils/HOC/FixedHeaders'

type AddFormPackageProcessorDialogProps = {
	processors: ConfigurableFormPackageProcessor[]
	packageId: number
	onConfirm: (instance: BaseFormPackageProcessorInstanceModel) => void
	onCancel: () => void
}

export const AddFormPackageProcessorDialog = (
	props: AddFormPackageProcessorDialogProps,
) => {
	const [selectedProcessorId, setSelectedProcessorId] = useState<string>(
		props.processors[0].processorId ?? undefined,
	)

	const validationSchema = yup.object({
		// we don't have a unique constraint on names here because of the way we handle processor versions
		// we really only care about ids in this case
		name: yup.string().required('Name is a required field'),
		description: yup.string(),
	})

	const formik = useFormik({
		initialValues: {
			name: '',
			description: '',
		},
		validationSchema: validationSchema,
		onSubmit: async (values) => {
			props.onConfirm({
				name: values.name,
				description: values.description,
				processorId: selectedProcessorId,
				state: {},
			})
		},
	})

	return (
		<>
			<DialogTitle>Add Form Package Processor</DialogTitle>
			<DialogContent>
				<Box display="flex" flexDirection="column" gap={2}>
					<FixedHeaderWithHint
						label="Name"
						hint={formik.touched.name && formik.errors.name}
						hintColor="error.main"
					>
						<TextField
							fullWidth
							id="name"
							name="name"
							value={formik.values.name}
							error={!!formik.errors.name && formik.touched.name}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={formik.isSubmitting}
							autoComplete="off"
						/>
					</FixedHeaderWithHint>
					<FixedHeaderWithHint label="Description">
						<TextField
							fullWidth
							id="description"
							name="description"
							value={formik.values.description}
							error={!!formik.errors.description && formik.touched.description}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={formik.isSubmitting}
							autoComplete="off"
						/>
					</FixedHeaderWithHint>
					<Select
						value={selectedProcessorId}
						onChange={(evt) => setSelectedProcessorId(evt.target.value)}
					>
						{props.processors.map((processor) => (
							<MenuItem
								key={processor.processorId}
								value={processor.processorId ?? ''}
							>
								<div>
									<Typography fontWeight="bold">
										{processor.displayName}
									</Typography>
									<Typography fontSize="small" fontStyle="italic">
										{processor.description}
									</Typography>
								</div>
							</MenuItem>
						))}
					</Select>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={props.onCancel}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={formik.submitForm}
					disabled={
						!formik.dirty ||
						!formik.isValid ||
						selectedProcessorId === undefined ||
						formik.isSubmitting
					}
				>
					Confirm
				</Button>
			</DialogActions>
		</>
	)
}
